<template lang="pug">
.pdf-field-editor
  //- LEFT: interactive PDF. Click any blank spot to drop a new field.
  .editor-left
    .toolbar
      .hint {{ $t(`${baseTranslationPath}.click_to_add_hint`) }}
      .zoom
        aiq-button(size="small" @click="setScale(renderScale - 0.15)") −
        span.zoom-label {{ Math.round(renderScale * 100) }}%
        aiq-button(size="small" @click="setScale(renderScale + 0.15)") +
    .pages-scroll(v-loading="loading")
      .load-error(v-if="loadError") {{ loadError }}
      .pdf-page(
        v-for="page in pages"
        :key="page.pageNumber"
        :style="{ width: page.width + 'px', height: page.height + 'px' }"
      )
        //- pdf.js renders here
        canvas(:ref="el => setCanvasRef(page.pageNumber, el)")
        //- transparent capture + boxes layer, exactly overlapping the canvas
        .overlay(@click="onBlankClick(page, $event)")
          //- native widgets extracted from the PDF (geometry from pdf.js). Blue when
          //- a stored field maps to them, gray when no field captured them (missed).
          .field-box.existing(
            v-for="box in existingForPage(page.pageNumber)"
            :key="box.id"
            :class="{ selected: box.id === selectedBoxId, highlighted: isHighlighted(box), mapped: isExistingMapped(box), orphan: !isExistingMapped(box) }"
            :style="boxStyle(box)"
            @click.stop="onBoxClick(box)"
          )
            .field-box-label(v-if="showLabel(box)") {{ box.name || box.fieldType }}
          //- saved manual fields: draggable + resizable, click to configure.
          .field-box.added(
            v-for="box in boxesForPage(page.pageNumber)"
            :key="box.id"
            :class="{ selected: box.id === selectedBoxId, highlighted: isHighlighted(box) }"
            :style="boxStyle(box)"
            @mousedown.stop="onBoxPointerDown(box, $event)"
            @click.stop="onBoxClick(box)"
          )
            .field-box-label(v-if="showLabel(box)") {{ box.name || box.fieldType }}
            template(v-if="box.id === selectedBoxId")
              .resize-handle(
                v-for="h in resizeHandles"
                :key="h"
                :class="'handle-' + h"
                @mousedown.stop="onHandlePointerDown(box, h, $event)"
                @click.stop
              )
          //- the in-progress draft box: first click builds it here, resize/drag it,
          //- then click it again to open the popup and save it as a field.
          .field-box.draft(
            v-if="draftBox && draftBox.page === page.pageNumber"
            :class="{ selected: draftBox.id === selectedBoxId }"
            :style="boxStyle(draftBox)"
            @mousedown.stop="onBoxPointerDown(draftBox, $event)"
            @click.stop="onBoxClick(draftBox)"
          )
            .field-box-label {{ draftBox.name || $t(`${baseTranslationPath}.draft_label`) }}
            .resize-handle(
              v-for="h in resizeHandles"
              :key="h"
              :class="'handle-' + h"
              @mousedown.stop="onHandlePointerDown(draftBox, h, $event)"
              @click.stop
            )

  //- Popup: add (blank click) or edit/configure (box click) a field.
  aiq-dialog.add-field-modal(
    :title="isEditMode ? $t(`${baseTranslationPath}.edit_field_title`) : $t(`${baseTranslationPath}.add_field_title`)"
    v-model="showAddField"
    :show-close="true"
    :close-on-click-modal="false"
    width="420px"
    @closed="resetPending"
  )
    .add-field-form(v-if="pending")
      .field-row
        label {{ $t(`${baseTranslationPath}.field_name`) }}
        aiq-input(v-model="pending.name" :placeholder="$t(`${baseTranslationPath}.field_name_ph`)")
      .field-row
        label {{ $t(`${baseTranslationPath}.field_type`) }}
        aiq-select(v-model="pending.fieldType" style="width: 100%")
          aiq-option(v-for="t in fieldTypeOptions" :key="t" :label="t" :value="t")
      .field-row
        label {{ $t(`${baseTranslationPath}.actor`) }}
        aiq-select(v-model="pending.actor" style="width: 100%")
          aiq-option(v-for="a in actorOptions" :key="a" :label="a" :value="a")
      .field-row.inline
        aiq-switch(v-model="pending.required")
        span {{ $t(`${baseTranslationPath}.required`) }}
      .field-row.inline(v-if="!isEditMode && selectedQuestionKey")
        aiq-switch(v-model="pending.attach")
        span {{ $t(`${baseTranslationPath}.attach_to_question`) }}
      .field-row.coords
        span {{ $t(`${baseTranslationPath}.coordinates`) }}:
        span.mono page {{ pending.page }} — [{{ round(pending.rect.x0) }}, {{ round(pending.rect.y0) }}, {{ round(pending.rect.x1) }}, {{ round(pending.rect.y1) }}]
      .actions
        aiq-button(@click="cancelAdd") {{ $t('global.cancel') }}
        aiq-button(type="primary" :loading="saving" :disabled="!canConfirm" @click="confirmAdd") {{ isEditMode ? $t(`${baseTranslationPath}.save_field`) : $t(`${baseTranslationPath}.add_field`) }}
</template>

<script>
// pdfjs-dist v3 "legacy" build + its webpack worker entry: works with the
// project's older (openssl-legacy-provider) webpack toolchain, unlike the
// ESM-only v4+ builds. Run `yarn add pdfjs-dist@3.11.174` before using.
import { markRaw } from 'vue';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import axios from 'axios';
import get from 'lodash/get';
import { getUrl } from '@/config/api.routes';

// Run pdf.js in a REAL web worker loaded from a served file (copied into
// public/ from pdfjs-dist@3.11.174, so its version matches the API import).
// This avoids the fake/main-thread worker, which bundles a second copy of the
// pdfjs core and triggers "Cannot read private member #pagePromises ...".
// Keep public/pdf.worker.min.js in sync if pdfjs-dist is upgraded.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.BASE_URL || '/'}pdf.worker.min.js`;

// S3 byte download uses a bare axios instance so the app-wide interceptor does
// NOT attach the base-api Authorization header to the S3 request (which would
// trip a CORS preflight / signature mismatch).
const s3Client = axios.create();
const PUBLIC_ACL = 'public';

// Default size of a freshly dropped box, in PDF points (top-left origin).
const DEFAULT_BOX_W = 160;
const DEFAULT_BOX_H = 16;

// Rounding + zoom bounds.
const ROUND_FACTOR = 10; // 1 decimal place
const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_PRECISION = 100;

// Minimum box size (PDF points) when resizing, and the pointer travel (canvas
// px) that separates a click (opens the popup) from a drag (move/resize).
const MIN_BOX_PT = 6;
const DRAG_THRESHOLD_PX = 3;
// A native widget whose geometry overlaps a mapped field by at least this IoU is
// considered "the same blank" — mirrors the backend GEOMETRY_OVERLAP_THRESHOLD
// (ai-manager-backend/src/helpers/forms/formsFields.js). Rects are never pixel
// perfect between pdf.js extraction and the stored field, so we match on overlap.
const MATCH_IOU_THRESHOLD = 0.5;
// Resize handles: corners + edge midpoints (compass directions).
const RESIZE_HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

// Intersection-over-union of two top-left-origin rects {x0,y0,x1,y1}. Mirrors the
// backend rectIoU so front/back agree on what counts as "the same box".
const rectIoU = (a, b) => {
  if (!a || !b) {
    return 0;
  }
  const inter = Math.max(0, Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0))
    * Math.max(0, Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0));
  if (inter === 0) {
    return 0;
  }
  const areaA = Math.max(0, a.x1 - a.x0) * Math.max(0, a.y1 - a.y0);
  const areaB = Math.max(0, b.x1 - b.x0) * Math.max(0, b.y1 - b.y0);
  const union = areaA + areaB - inter;
  return union > 0 ? inter / union : 0;
};

// Mirrors the backend field_type enum (see agentForms store addFormField docs).
const FIELD_TYPE_OPTIONS = [
  'text', 'checkbox', 'radio', 'date', 'currency', 'phone', 'email',
  'account_number', 'routing_number', 'signature', 'initials',
  'table_cell', 'computed', 'internal_only',
];

// Who is expected to fill the field; maps to schema field.actor.
const ACTOR_OPTIONS = ['customer', 'agent', 'system'];

// pdf.js Widget annotation fieldType -> our field_type.
const ANNOTATION_TYPE_MAP = {
  Tx: 'text',
  Ch: 'text',
  Sig: 'signature',
};

// A pdf.js Widget annotation with fieldType 'Btn' is a checkbox unless it
// carries radio/pushbutton flags.
const annotationFieldType = (annotation) => {
  if (annotation.fieldType === 'Btn') {
    if (annotation.radioButton || annotation.checkBox === false) {
      return 'radio';
    }
    return 'checkbox';
  }
  return ANNOTATION_TYPE_MAP[annotation.fieldType] || 'text';
};

let boxSeq = 0;

export default {
  name: 'PdfFieldEditor',
  props: {
    // base-api file id, e.g. formDetails.external_file_id -> /files/:id
    fileId: {
      type: [String, Number],
      required: true,
    },
    // POST /forms/:id/fields target when placing a new field.
    formId: {
      type: [String, Number],
      default: null,
    },
    // field names (native annotation fieldName / field_key) of the currently
    // selected question; only their boxes render a label.
    highlightNames: {
      type: Array,
      default: () => [],
    },
    // schema question_id (== form_questions.question_key) of the selected
    // question, used to attach a newly placed field to it.
    selectedQuestionKey: {
      type: [String, Number],
      default: null,
    },
    // the form's persisted fields (GET /forms/:id/fields). Manual fields carry
    // rect/page, which we rehydrate into boxes so manually-placed boxes survive
    // a reload — image-only PDFs have no native widgets to redraw from.
    fields: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['field-added', 'field-updated'],
  data() {
    return {
      baseTranslationPath: 'iq_tools_tab.forms_tab.editor',
      pdfDoc: null,
      // pages: [{ pageNumber, width, height }]
      pages: [],
      // canvasRefs: pageNumber -> canvas el
      canvasRefs: {},
      // renderScale: canvas px per PDF point
      renderScale: 1.3,
      loading: false,
      loadError: '',
      // existingFields: native widgets already in the PDF (from getAnnotations)
      existingFields: [],
      // placedFields: [{ id, page, rect{x0,y0,x1,y1}, fieldType, name }]
      placedFields: [],
      // draftBox: an unsaved box being placed. First blank click builds it (no
      // dialog); clicking it opens the popup to name + persist it as a field.
      draftBox: null,
      selectedBoxId: null,
      // interaction: transient drag/resize state (null when idle).
      interaction: null,
      // swallow the click that fires right after a drag/resize (and the overlay
      // click if the pointer finished over blank canvas) so it doesn't open the
      // popup / drop a second draft.
      suppressClickPopup: false,
      suppressBlank: false,
      resizeHandles: RESIZE_HANDLES,
      showAddField: false,
      // pending: draft box awaiting confirmation
      pending: null,
      fieldTypeOptions: FIELD_TYPE_OPTIONS,
      actorOptions: ACTOR_OPTIONS,
      saving: false,
    };
  },
  computed: {
    isEditMode() {
      return get(this.pending, 'mode') === 'edit';
    },
    // add requires a name + type; edit only needs a type.
    canConfirm() {
      if (!this.pending || !this.pending.fieldType) {
        return false;
      }
      return this.isEditMode || !!(this.pending.name && this.pending.name.trim());
    },
    highlightNameSet() {
      return new Set((this.highlightNames || []).filter(Boolean));
    },
    // ids of native (extracted) boxes that a stored field maps to. Native AcroForm
    // widgets match a field by name (reliable, always present); we also accept a
    // geometry overlap as a fallback (rects are never pixel-perfect). Anything not
    // matched is an orphan widget we missed capturing — rendered gray.
    mappedExistingIds() {
      const nameSet = new Set();
      (this.fields || []).forEach((f) => {
        if (f && f.field_name) {
          nameSet.add(f.field_name);
        }
        if (f && f.field_key) {
          nameSet.add(f.field_key);
        }
      });
      const geomFields = (this.fields || []).filter(f => f && f.rect && f.page !== null && f.page !== undefined);
      const ids = new Set();
      this.existingFields.forEach((box) => {
        const byName = !!box.name && nameSet.has(box.name);
        const byGeom = geomFields.some(
          f => f.page === box.page && rectIoU(f.rect, box.rect) > MATCH_IOU_THRESHOLD,
        );
        if (byName || byGeom) {
          ids.add(box.id);
        }
      });
      return ids;
    },
  },
  watch: {
    fileId() {
      this.loadPdf();
    },
    fields: {
      immediate: true,
      handler() {
        this.rehydratePlacedFields();
      },
    },
  },
  async mounted() {
    this._onKeyDown = this.onKeyDown.bind(this);
    document.addEventListener('keydown', this._onKeyDown);
    await this.loadPdf();
  },
  beforeUnmount() {
    if (this.pdfDoc) {
      this.pdfDoc.destroy();
    }
    this.removeDragListeners();
    document.removeEventListener('keydown', this._onKeyDown);
  },
  methods: {
    round(n) {
      return Math.round(n * ROUND_FACTOR) / ROUND_FACTOR;
    },
    setCanvasRef(pageNumber, el) {
      if (el) {
        this.canvasRefs[pageNumber] = el;
      }
    },
    boxesForPage(pageNumber) {
      return this.placedFields.filter(b => b.page === pageNumber);
    },
    // Rebuild placedFields from the persisted field list. Manually-placed fields
    // carry rect/page (projected from schema_json); native widgets are redrawn
    // from the PDF's own annotations, so only manual fields are rehydrated here
    // to avoid double-drawing. This is what makes manual boxes survive a reload.
    rehydratePlacedFields() {
      this.placedFields = (this.fields || [])
        .filter(f => f && f.source === 'manual' && f.rect && f.page !== null && f.page !== undefined)
        .map(f => ({
          id: `saved-${f.id}`,
          fieldId: f.id,
          fieldKey: f.field_key,
          source: 'manual',
          page: f.page,
          rect: {
            x0: f.rect.x0,
            y0: f.rect.y0,
            x1: f.rect.x1,
            y1: f.rect.y1,
          },
          fieldType: f.field_type,
          name: f.field_name || f.field_key,
          actor: f.actor || 'customer',
          required: !!f.required,
        }));
    },
    existingForPage(pageNumber) {
      return this.existingFields.filter(b => b.page === pageNumber);
    },
    // A box belongs to the selected question when its name matches one of the
    // question's field names.
    isHighlighted(box) {
      return !!box.name && this.highlightNameSet.has(box.name);
    },
    // Only label the selected box and the selected question's fields, so the
    // page isn't cluttered with every field name at once.
    showLabel(box) {
      return box.id === this.selectedBoxId || this.isHighlighted(box);
    },
    isExistingMapped(box) {
      return this.mappedExistingIds.has(box.id);
    },
    // PDF-point dimensions of a page (rects are stored in points, not canvas px).
    pagePointDims(pageNumber) {
      const page = this.pages.find(p => p.pageNumber === pageNumber);
      const s = this.renderScale;
      return page ? { w: page.width / s, h: page.height / s } : { w: Infinity, h: Infinity };
    },
    // --- drag / resize -------------------------------------------------------
    // Esc clears the current selection: it discards an unsaved draft box and
    // deselects the active box so its name label stops showing.
    onKeyDown(event) {
      if (event.key !== 'Escape') {
        return;
      }
      // Let the field popup handle its own Esc (close dialog) first.
      if (this.showAddField) {
        return;
      }
      if (!this.draftBox && this.selectedBoxId === null) {
        return;
      }
      this.draftBox = null;
      this.selectedBoxId = null;
    },
    // Pointer down on a box body starts a potential move; small travel is still a
    // click (opens the popup), larger travel becomes a drag.
    onBoxPointerDown(box, event) {
      this.selectedBoxId = box.id;
      this.suppressClickPopup = false;
      this.interaction = {
        mode: 'move',
        box,
        startX: event.clientX,
        startY: event.clientY,
        startRect: { ...box.rect },
        moved: false,
      };
      this.addDragListeners();
    },
    // Pointer down on a resize handle always resizes (never opens the popup).
    onHandlePointerDown(box, handle, event) {
      this.selectedBoxId = box.id;
      this.suppressClickPopup = false;
      this.interaction = {
        mode: 'resize',
        handle,
        box,
        startX: event.clientX,
        startY: event.clientY,
        startRect: { ...box.rect },
        moved: true,
      };
      this.addDragListeners();
    },
    addDragListeners() {
      this._onDragMove = this.onDragMove.bind(this);
      this._onDragUp = this.onDragUp.bind(this);
      document.addEventListener('mousemove', this._onDragMove);
      document.addEventListener('mouseup', this._onDragUp);
    },
    removeDragListeners() {
      document.removeEventListener('mousemove', this._onDragMove);
      document.removeEventListener('mouseup', this._onDragUp);
    },
    onDragMove(event) {
      const it = this.interaction;
      if (!it) {
        return;
      }
      const s = this.renderScale;
      if (!it.moved
        && Math.hypot(event.clientX - it.startX, event.clientY - it.startY) > DRAG_THRESHOLD_PX) {
        it.moved = true;
      }
      if (!it.moved) {
        return;
      }
      const dx = (event.clientX - it.startX) / s;
      const dy = (event.clientY - it.startY) / s;
      const dims = this.pagePointDims(it.box.page);
      it.box.rect = it.mode === 'move'
        ? this.moveRect(it.startRect, dx, dy, dims)
        : this.resizeRect(it.startRect, it.handle, dx, dy, dims);
    },
    onDragUp() {
      this.removeDragListeners();
      const it = this.interaction;
      this.interaction = null;
      if (!it) {
        return;
      }
      if (it.moved) {
        // the click/overlay-click that follows must not open the popup or drop a
        // new draft; persist the new geometry for already-saved boxes.
        this.suppressClickPopup = true;
        this.suppressBlank = true;
        this.persistGeometry(it.box);
      }
    },
    // Translate a rect by (dx, dy) points, clamped inside the page.
    moveRect(start, dx, dy, dims) {
      const w = start.x1 - start.x0;
      const h = start.y1 - start.y0;
      let x0 = Math.min(Math.max(0, start.x0 + dx), dims.w - w);
      let y0 = Math.min(Math.max(0, start.y0 + dy), dims.h - h);
      if (!Number.isFinite(x0)) {
        x0 = start.x0;
      }
      if (!Number.isFinite(y0)) {
        y0 = start.y0;
      }
      return { x0, y0, x1: x0 + w, y1: y0 + h };
    },
    // Move the edge(s) named by the compass handle, enforcing a minimum size and
    // clamping to the page.
    resizeRect(start, handle, dx, dy, dims) {
      let { x0, y0, x1, y1 } = start;
      if (handle.includes('w')) {
        x0 = Math.min(x0 + dx, x1 - MIN_BOX_PT);
      }
      if (handle.includes('e')) {
        x1 = Math.max(x1 + dx, x0 + MIN_BOX_PT);
      }
      if (handle.includes('n')) {
        y0 = Math.min(y0 + dy, y1 - MIN_BOX_PT);
      }
      if (handle.includes('s')) {
        y1 = Math.max(y1 + dy, y0 + MIN_BOX_PT);
      }
      x0 = Math.max(0, x0);
      y0 = Math.max(0, y0);
      x1 = Math.min(dims.w, x1);
      y1 = Math.min(dims.h, y1);
      return { x0, y0, x1, y1 };
    },
    // Persist a moved/resized box. Drafts (no fieldId yet) are saved via the
    // popup instead, so there is nothing to PUT.
    async persistGeometry(box) {
      if (!box.fieldId || !this.formId) {
        return;
      }
      try {
        await this.$store.dispatch('agentForms/updateFormField', {
          formId: this.formId,
          fieldId: box.fieldId,
          rect: { ...box.rect },
          page: box.page,
        });
        this.$emit('field-updated');
      } catch (e) {
        this.$aiq.notify.error(
          `${this.$t(`${this.baseTranslationPath}.field_update_error`)} (${get(e, 'response.data.message', get(e, 'message', ''))})`,
        );
        this.$emit('field-updated');
      }
    },
    // A plain click (no drag) on a box opens the popup; the click right after a
    // drag is swallowed.
    onBoxClick(box) {
      if (this.suppressClickPopup) {
        this.suppressClickPopup = false;
        return;
      }
      this.openBoxPopup(box);
    },
    annotationToBox(annotation, pageNumber, page) {
      // Convert the annotation rect (PDF user space, bottom-left origin) to our
      // top-left PDF points via a scale-1 viewport: at scale 1, viewport pixels
      // equal PDF points and the Y-flip is already applied. Same convention the
      // click handler uses, so existing + added boxes share one coordinate space.
      const vp = page.getViewport({ scale: 1 });
      const [ax, ay, bx, by] = vp.convertToViewportRectangle(annotation.rect);
      const rect = {
        x0: Math.min(ax, bx),
        y0: Math.min(ay, by),
        x1: Math.max(ax, bx),
        y1: Math.max(ay, by),
      };
      if (rect.x1 <= rect.x0 || rect.y1 <= rect.y0) {
        return null;
      }
      return {
        id: `native-${annotation.id}`,
        page: pageNumber,
        rect,
        fieldType: annotationFieldType(annotation),
        name: annotation.fieldName || '',
        source: 'native',
      };
    },
    boxStyle(box) {
      const s = this.renderScale;
      return {
        left: `${box.rect.x0 * s}px`,
        top: `${box.rect.y0 * s}px`,
        width: `${(box.rect.x1 - box.rect.x0) * s}px`,
        height: `${(box.rect.y1 - box.rect.y0) * s}px`,
      };
    },

    async loadPdf() {
      if (!this.fileId) {
        return;
      }
      this.loading = true;
      this.loadError = '';
      this.pages = [];
      this.canvasRefs = {};
      try {
        // 1. GET /files/:id returns metadata (not bytes): the S3 payload.
        const metaResp = await axios.get(getUrl('files.base', this.fileId));
        const payload = get(metaResp, 'data.payload', {});

        // 2. Resolve a downloadable URL. Public assets expose a direct url;
        //    private ones must be presigned via base-api (POST /files/presigned).
        let fileUrl = get(payload, 'url', '');
        if (get(payload, 'acl') !== PUBLIC_ACL) {
          const presigned = await axios.post(`${getUrl('files.base')}/presigned`, {
            bucket: get(payload, 'bucket'),
            key: get(payload, 'key'),
          });
          fileUrl = get(presigned, 'data.url', '');
        }

        // 3. Download the actual PDF bytes from S3 and hand them to pdf.js.
        //    Wrap in a Uint8Array (pdf.js is happiest with a typed array).
        const { data } = await s3Client.get(fileUrl, { responseType: 'arraybuffer' });
        // markRaw: pdf.js objects use private class fields (#pagePromises, ...)
        // that break if Vue wraps them in a reactive Proxy. Keep them raw.
        this.pdfDoc = markRaw(await pdfjsLib.getDocument({
          data: new Uint8Array(data),
          isEvalSupported: false,
        }).promise);

        const pages = [];
        const existing = [];
        for (let n = 1; n <= this.pdfDoc.numPages; n += 1) {
          const page = await this.pdfDoc.getPage(n);
          const viewport = page.getViewport({ scale: this.renderScale });
          pages.push({ pageNumber: n, width: viewport.width, height: viewport.height });
          // pdf.js hands us the native form-field widgets and their geometry.
          const annotations = await page.getAnnotations();
          annotations
            .filter(a => a.subtype === 'Widget' && a.rect)
            .forEach((a) => {
              const box = this.annotationToBox(a, n, page);
              if (box) {
                existing.push(box);
              }
            });
        }
        this.existingFields = existing;
        this.pages = pages;
        await this.$nextTick();
        await this.renderAllPages();
      } catch (e) {
        // Surface the real cause (worker setup, parse error, etc.) for the prototype.
        // eslint-disable-next-line no-console
        console.error('[PdfFieldEditor] loadPdf failed:', e);
        this.loadError = `${this.$t(`${this.baseTranslationPath}.load_error`)} (${get(e, 'message', e)})`;
      } finally {
        this.loading = false;
      }
    },

    async renderAllPages() {
      if (!this.pdfDoc) {
        return;
      }
      for (let n = 1; n <= this.pdfDoc.numPages; n += 1) {
        const canvas = this.canvasRefs[n];
        if (!canvas) {
          continue;
        }
        const page = await this.pdfDoc.getPage(n);
        const viewport = page.getViewport({ scale: this.renderScale });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        await page.render({ canvasContext: ctx, viewport }).promise;
      }
    },

    async setScale(next) {
      const clamped = Math.min(
        MAX_SCALE,
        Math.max(MIN_SCALE, Math.round(next * SCALE_PRECISION) / SCALE_PRECISION),
      );
      if (clamped === this.renderScale) {
        return;
      }
      this.renderScale = clamped;
      // page pixel dimensions depend on scale; recompute then repaint.
      const pages = this.pages.map(p => ({ ...p }));
      for (let i = 0; i < pages.length; i += 1) {
        const page = await this.pdfDoc.getPage(pages[i].pageNumber);
        const vp = page.getViewport({ scale: this.renderScale });
        pages[i].width = vp.width;
        pages[i].height = vp.height;
      }
      this.pages = pages;
      await this.$nextTick();
      await this.renderAllPages();
    },

    onBlankClick(page, event) {
      // A click that is really the tail of a drag/resize must not drop a box.
      if (this.suppressBlank) {
        this.suppressBlank = false;
        return;
      }
      // Click position relative to the overlay's top-left, in canvas pixels.
      const rect = event.currentTarget.getBoundingClientRect();
      const px = event.clientX - rect.left;
      const py = event.clientY - rect.top;

      // Convert to PDF points, top-left origin (PyMuPDF convention). Because the
      // canvas is already rendered top-left with height = pageHeightPt * scale,
      // dividing by scale lands directly in PyMuPDF coords — no Y-flip needed.
      const s = this.renderScale;
      const pageW = page.width / s;
      const pageH = page.height / s;
      let x0 = px / s;
      let y0 = py / s;
      // keep the default box inside the page
      x0 = Math.min(x0, pageW - DEFAULT_BOX_W);
      y0 = Math.min(y0, pageH - DEFAULT_BOX_H);
      x0 = Math.max(0, x0);
      y0 = Math.max(0, y0);

      // First click only BUILDS the box (draggable + resizable). The popup opens
      // on the next click of the box, so the user can position/size it first.
      this.draftBox = {
        id: `draft-${boxSeq += 1}`,
        source: 'manual',
        isDraft: true,
        page: page.pageNumber,
        rect: {
          x0,
          y0,
          x1: x0 + DEFAULT_BOX_W,
          y1: y0 + DEFAULT_BOX_H,
        },
        fieldType: 'text',
        name: '',
        actor: 'customer',
        required: false,
      };
      this.selectedBoxId = this.draftBox.id;
    },

    async confirmAdd() {
      if (!this.canConfirm) {
        return;
      }
      const { mode, attach, ...box } = this.pending;
      const clean = { ...box, rect: { ...box.rect } };
      if (mode === 'edit') {
        this.updateBox(clean);
        this.selectedBoxId = clean.id;
        this.showAddField = false;
        return;
      }
      // New field: persist it via POST /forms/:id/fields, optionally attaching
      // it to the selected question (schema question_id === question_key).
      this.saving = true;
      try {
        const body = {
          formId: this.formId,
          field_name: clean.name.trim(),
          field_type: clean.fieldType,
          required: !!clean.required,
          actor: clean.actor,
          page: clean.page,
          rect: clean.rect,
        };
        if (attach && this.selectedQuestionKey) {
          body.question_id = this.selectedQuestionKey;
        }
        await this.$store.dispatch('agentForms/addFormField', body);
        this.draftBox = null;
        this.selectedBoxId = null;
        this.showAddField = false;
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.field_added`));
        this.$emit('field-added', clean);
      } catch (e) {
        this.$aiq.notify.error(
          `${this.$t(`${this.baseTranslationPath}.field_add_error`)} (${get(e, 'response.data.message', get(e, 'message', ''))})`,
        );
      } finally {
        this.saving = false;
      }
    },

    // Write an edited field back into whichever list holds it.
    updateBox(box) {
      const list = box.source === 'native' ? this.existingFields : this.placedFields;
      const idx = list.findIndex(b => b.id === box.id);
      if (idx !== -1) {
        list.splice(idx, 1, box);
      }
    },

    cancelAdd() {
      this.showAddField = false;
    },

    resetPending() {
      this.pending = null;
    },

    // Clicking a box opens the popup. A draft opens in ADD mode (first save
    // persists it); an existing/saved box opens in EDIT mode.
    openBoxPopup(box) {
      this.selectedBoxId = box.id;
      this.pending = {
        mode: box.isDraft ? 'add' : 'edit',
        source: box.source || 'manual',
        id: box.id,
        fieldId: box.fieldId,
        fieldKey: box.fieldKey,
        page: box.page,
        rect: { ...box.rect },
        fieldType: box.fieldType,
        name: box.name || '',
        actor: box.actor || 'customer',
        required: !!box.required,
        attach: false,
      };
      this.showAddField = true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./../../../styles/aiq-variables.scss";

.pdf-field-editor {
  display: flex;
  height: 100%;
  min-height: 0;
  gap: 8px;
}

.editor-left {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background-color: $aiq-bgc-list-item;
  border-radius: 4px;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid $aiq-bdc-input;

  .hint {
    font-size: 12px;
    color: $aiq-color-text-secondary;
  }

  .zoom {
    display: flex;
    align-items: center;
    gap: 6px;

    .zoom-label {
      font-size: 12px;
      min-width: 38px;
      text-align: center;
    }
  }
}

.pages-scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.load-error {
  color: $aiq-color-error;
  padding: 24px;
}

.pdf-page {
  position: relative;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
  background: #fff;

  canvas {
    display: block;
  }

  .overlay {
    position: absolute;
    inset: 0;
    cursor: crosshair;
  }
}

.field-box {
  position: absolute;
  cursor: pointer;
  box-sizing: border-box;

  // Existing native widgets extracted from the PDF.
  &.existing {
    border: 1px solid transparent;

    // Blue: a stored field maps to this widget (overlap match).
    &.mapped {
      background: rgba(64, 158, 255, 0.18);

      &:hover {
        background: rgba(64, 158, 255, 0.28);
        border-color: rgba(64, 158, 255, 0.6);
      }
    }

    // Gray: the extractor found a widget here but no field captured it — a
    // likely miss the user may want to map.
    &.orphan {
      background: rgba(120, 120, 120, 0.22);

      &:hover {
        background: rgba(120, 120, 120, 0.34);
        border-color: rgba(120, 120, 120, 0.7);
      }
    }
  }

  // Newly added fields: always outlined so they read as user-placed.
  &.added {
    border: 1.5px solid $aiq-bgc-button-primary;
    background: rgba(64, 158, 255, 0.12);
    cursor: move;
  }

  // Draft: an unsaved box being positioned before the popup saves it.
  &.draft {
    border: 1.5px dashed $aiq-bgc-button-primary;
    background: rgba(64, 158, 255, 0.1);
    cursor: move;
  }

  &.selected {
    border: 1.5px solid $aiq-bgc-button-primary;
    background: rgba(64, 158, 255, 0.32);
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.35);
  }

  &.highlighted {
    border-color: $aiq-bgc-button-primary;
    background: rgba(64, 158, 255, 0.3);
  }

  .field-box-label {
    position: absolute;
    top: -16px;
    left: 0;
    font-size: 10px;
    line-height: 14px;
    padding: 0 4px;
    color: #fff;
    background: $aiq-bgc-button-primary;
    white-space: nowrap;
    border-radius: 2px;
  }

  // 8 drag handles on the selected/draft box edges + corners (window-style).
  .resize-handle {
    position: absolute;
    width: 8px;
    height: 8px;
    background: #fff;
    border: 1px solid $aiq-bgc-button-primary;
    border-radius: 1px;
    box-sizing: border-box;
    z-index: 2;

    &.handle-nw { top: -4px; left: -4px; cursor: nwse-resize; }
    &.handle-n  { top: -4px; left: calc(50% - 4px); cursor: ns-resize; }
    &.handle-ne { top: -4px; right: -4px; cursor: nesw-resize; }
    &.handle-e  { top: calc(50% - 4px); right: -4px; cursor: ew-resize; }
    &.handle-se { bottom: -4px; right: -4px; cursor: nwse-resize; }
    &.handle-s  { bottom: -4px; left: calc(50% - 4px); cursor: ns-resize; }
    &.handle-sw { bottom: -4px; left: -4px; cursor: nesw-resize; }
    &.handle-w  { top: calc(50% - 4px); left: -4px; cursor: ew-resize; }
  }
}


.add-field-form {
  display: flex;
  flex-direction: column;
  gap: 14px;

  .field-row {
    display: flex;
    flex-direction: column;
    gap: 4px;

    label { font-size: 12px; font-weight: 600; }

    &.inline {
      flex-direction: row;
      align-items: center;
      gap: 8px;
      font-size: 12px;
    }
  }

  .coords {
    flex-direction: row;
    gap: 6px;
    font-size: 12px;
    color: $aiq-color-text-secondary;

    .mono { font-family: monospace; }
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
}
</style>
